'use es6';

import { Component } from 'react';
import enviro from 'enviro';
import inpageActionTypes from 'ContentEditorUI/redux/actions/inpageActionTypes';
import { captureMessage } from 'ContentEditorUI/lib/exceptions';
import { AsyncComponentReload } from 'ContentEditorUI/components/shared/makeAsyncUtils';
import { EditorMetricsTracker } from 'ContentEditorUI/utils/metricsTracker';
import { jsx as _jsx } from "react/jsx-runtime";
let isFirstLameNullErrorHandlerToMount = true;
let numberOfLameReactErrorsInIframe = 0;

// Only show shameful message:
//   - One time (including parent app and preview frame)
//   - Only on QA and local dev (not prod)
export const logLameErrorComponentMessageToConsole = () => {
  if (window.hsInEditor) {
    throw new Error('logLameErrorComponentMessageToConsole incorrectly called from inside the preview frame');
  } else if (isFirstLameNullErrorHandlerToMount) {
    isFirstLameNullErrorHandlerToMount = false;
    if (!enviro.deployed('dev-help') || enviro.isQa('dev-help')) {
      console.warn('Editor code is still using LameNullErrorHandler, 😓. We really should work on eliminating it!');
    }
  }
};
const sendActionToParent = message => {
  if (window.hs && window.hs.app && window.hs.app.eventBus) {
    window.hs.app.eventBus.postMessage(message);
  }
};

// Need to serialize the error before sending it across postMessage (though I haven't figured out
// how to successfully get New Relic to ingest the stack trace)
const serializeError = error => {
  return {
    name: error.name,
    message: error.message,
    stack: error.stack
  };
};
const tellParentToLogLameErrorComponentMessageToConsole = () => {
  if (!window.hsInEditor) {
    throw new Error('tellParentToLogLameErrorComponentMessageToConsole only should be called from inside the preview frame');
  } else if (isFirstLameNullErrorHandlerToMount) {
    isFirstLameNullErrorHandlerToMount = false;
    sendActionToParent({
      action: inpageActionTypes.LOG_LAME_ERROR_MESSAGE_TO_CONSOLE
    });
  }
};
export const tellParentAboutReactErrorInIframe = (error, info) => {
  numberOfLameReactErrorsInIframe += 1;

  // After some limit, stop spamming the parent with more error info
  const tooManyErrorsBeingSentToParent = numberOfLameReactErrorsInIframe > 10;
  if (!tooManyErrorsBeingSentToParent) {
    sendActionToParent({
      action: inpageActionTypes.REACT_ERROR_IN_IFRAME,
      numberOfLameReactErrorsInIframe,
      error: serializeError(error),
      info
    });
  }
};
let numberOfSentryMessagesInIframe = 0;
export const tellParentAboutSentryMessageInIframe = (sentryMessage, info = {}) => {
  numberOfSentryMessagesInIframe += 1;
  const tooManySentryMessagesBeingSentToParent = numberOfSentryMessagesInIframe > 10;
  if (!tooManySentryMessagesBeingSentToParent) {
    sendActionToParent({
      action: inpageActionTypes.SENTRY_MESSAGE_IN_IFRAME,
      sentryMessage,
      info
    });
  }
};
let numberOfSentryExceptionsInIframe = 0;
export const tellParentAboutSentryExceptionInIframe = (error, extraInfo = {}) => {
  numberOfSentryExceptionsInIframe += 1;
  const tooManySentryMessagesBeingSentToParent = numberOfSentryExceptionsInIframe > 10;
  if (!tooManySentryMessagesBeingSentToParent) {
    sendActionToParent({
      action: inpageActionTypes.SENTRY_EXCEPTION_IN_IFRAME,
      error: serializeError(error),
      extraInfo
    });
  }
};
let numberOfSentryBreadcrumbsInIframe = 0;
export const tellParentAboutSentryBreadcrumbInIframe = crumb => {
  numberOfSentryBreadcrumbsInIframe += 1;
  const tooManySentryMessagesBeingSentToParent = numberOfSentryBreadcrumbsInIframe > 10;
  if (!tooManySentryMessagesBeingSentToParent) {
    sendActionToParent({
      action: inpageActionTypes.SENTRY_BREADCRUMB_IN_IFRAME,
      crumb
    });
  }
};
export const sendErrorToNewRelicAndSentry = ({
  error,
  info = {},
  isFromIframe = false
}) => {
  captureMessage(`${error.message || error} caught by error handler`, {
    extra: {
      error,
      info,
      isFromIframe
    }
  });
};

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children] (optional)
 * @param {Function} props.onError (required)
 */
export class BaseLameNullErrorHandler extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      caughtError: false
    };
  }
  static getDerivedStateFromError() {
    // Trigger render to return null on any error (but tracking/side-effects are done in `componentDidCatch`)
    return {
      caughtError: true
    };
  }
  // Note, componentDidCatch does not completely swallow the JS error, it still will bubble up and
  // default Sentry/New Relic will track it
  componentDidCatch(error, info) {
    const {
      onError
    } = this.props;
    onError(error, info);
  }
  render() {
    const {
      children
    } = this.props;
    const {
      caughtError
    } = this.state;
    if (caughtError) {
      return null;
    }
    return children;
  }
}

/**
 * @param {Object} props
 * @param {boolean} props.enabled (required)
 * @param {React.ReactNode} [props.children] (optional)
 */
export class LameNullErrorHandler extends Component {
  componentDidMount() {
    logLameErrorComponentMessageToConsole();
  }
  onError(error, info) {
    sendErrorToNewRelicAndSentry({
      error,
      info,
      isFromIframe: false
    });
  }
  render() {
    const {
      enabled,
      children
    } = this.props;
    if (!enabled) {
      return children;
    }
    return /*#__PURE__*/_jsx(BaseLameNullErrorHandler, {
      onError: this.onError,
      children: children
    });
  }
}

/**
 * @param {Object} props
 * @param {boolean} props.enabled (required)
 * @param {React.ReactNode} [props.children] (optional)
 */
LameNullErrorHandler.defaultProps = {
  enabled: true
};
export class LameNullErrorHandlerInIframe extends Component {
  componentDidMount() {
    tellParentToLogLameErrorComponentMessageToConsole();
  }
  onError(error, info) {
    // Sometimes (haven't tracked down why) these iframe errors are swallowed and not
    // logged later
    // console.error(error);
    tellParentAboutReactErrorInIframe(error, info);
  }
  render() {
    const {
      enabled,
      children
    } = this.props;
    if (!enabled) {
      return children;
    }
    return /*#__PURE__*/_jsx(BaseLameNullErrorHandler, {
      onError: this.onError,
      children: children
    });
  }
}

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children] (optional)
 * @param {React.ReactNode} [props.errorComponent] (optional)
 * @param {boolean} [props.isFromIframe] (optional)
 * @param {Object} [props.metricsReporting] (optional)
 */
LameNullErrorHandlerInIframe.defaultProps = {
  enabled: true
};
export class AsyncComponentErrorHandler extends Component {
  constructor() {
    super();
    this.state = {
      showErrorComponent: false
    };
    this.onError = this.onError.bind(this);
  }
  onError(error, info) {
    let errorToSend = error;
    let infoToSend = info;
    const {
      metricsReporting,
      isFromIframe
    } = this.props;
    if (metricsReporting && metricsReporting.componentName) {
      EditorMetricsTracker.counter(`${metricsReporting.componentName}-component-failed-to-load`).increment();
      errorToSend = `${metricsReporting.componentName}-component-failed-to-load`;
      infoToSend = {
        error,
        info
      };
    }
    if (isFromIframe) {
      tellParentAboutReactErrorInIframe(errorToSend, infoToSend);
    } else {
      sendErrorToNewRelicAndSentry({
        error: errorToSend,
        info: infoToSend,
        isFromIframe: false
      });
    }
    this.setState({
      showErrorComponent: true
    });
  }
  render() {
    const {
      children,
      errorComponent
    } = this.props;
    const {
      showErrorComponent
    } = this.state;
    if (showErrorComponent) {
      return errorComponent || /*#__PURE__*/_jsx(AsyncComponentReload, {});
    }
    return /*#__PURE__*/_jsx(BaseLameNullErrorHandler, {
      onError: this.onError,
      children: children
    });
  }
}